import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import RangePicker from "@/components/RangePicker";
import { adminApi } from "@/lib/api";
import { errText } from "@/lib/ranges";

// Pass 59 — Conversion tracking. Durable coverage from the Sent_* flags
// on each lead (survives deploys), the Google bridge-sheet duplicate
// check, and the in-memory send log (current process only).

const AMBER = "#8a6d3b";
const pct = (n, d) => (d ? `${Math.round((100 * n) / d)}%` : "—");

function CoverageTable({ coverage }) {
  const rows = [
    { key: "qualified", label: "Qualified lead", ...coverage.qualified },
    { key: "won", label: "Won", ...coverage.won },
  ];
  return (
    <div className="scw-table-wrap">
      <table className="scw-table" data-testid="capi-coverage">
        <thead><tr>
          <th>Event</th><th>Eligible leads</th><th>Sent to Google</th><th>Sent to Meta</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} data-testid={`capi-coverage-${r.key}`}>
              <td>{r.label}</td>
              <td>{r.eligible}</td>
              <td>{r.google} <span style={{ color: "var(--scw-muted)" }}>({pct(r.google, r.eligible)})</span></td>
              <td>{r.meta} <span style={{ color: "var(--scw-muted)" }}>({pct(r.meta, r.eligible)})</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BridgeCard({ bridge, fixLive }) {
  if (!bridge) {
    return (
      <p style={{ fontSize: 13, color: AMBER, margin: 0 }} data-testid="capi-bridge-unavailable">
        Could not read the Google bridge sheet just now. Try again in a few minutes.
      </p>
    );
  }
  const before = bridge.duplicates - bridge.duplicates_since_fix;
  const clean = bridge.duplicates_since_fix === 0;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 32 }} data-testid="capi-bridge">
      <div>
        <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Duplicates since the fix</p>
        <p style={{ fontSize: 30, margin: 0, fontWeight: 500, color: clean ? "inherit" : "#a13b3b" }} data-testid="capi-dupes-since-fix">
          {bridge.duplicates_since_fix}
        </p>
        <p style={{ fontSize: 11, color: "var(--scw-muted)", margin: "4px 0 0 0" }}>Since {fixLive}. Target: 0.</p>
      </div>
      <div>
        <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Before the fix</p>
        <p style={{ fontSize: 30, margin: 0, fontWeight: 500 }}>{before}</p>
      </div>
      <div>
        <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Rows / unique Order IDs</p>
        <p style={{ fontSize: 30, margin: 0, fontWeight: 500 }}>{bridge.rows} / {bridge.unique_order_ids}</p>
      </div>
    </div>
  );
}

function LogSummary({ title, counts }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <div>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>{title}</p>
      {total === 0 ? (
        <p style={{ fontSize: 13, color: "var(--scw-muted)", margin: 0 }}>None since the last restart.</p>
      ) : (
        <div style={{ display: "flex", gap: 16 }}>
          {Object.entries(counts).map(([k, v]) => (
            <div key={k}>
              <p style={{ fontSize: 22, margin: 0, fontWeight: 500 }}>{v}</p>
              <p style={{ fontSize: 11, margin: 0, color: "var(--scw-muted)", textTransform: "capitalize" }}>{k}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PLATFORM = { google: "Google", meta: "Meta" };

export default function AdminConversions() {
  const [range, setRange] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (params) => {
    setErr("");
    setLoading(true);
    try {
      const r = await adminApi.analyticsCapi(params || {});
      setData(r.data);
      setRange(r.data.range);
    } catch (e) {
      setErr(errText(e, "Failed to load."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRangeChange = (next) => { setRange(next); load(next); };

  const header = (
    <>
      <Link to="/admin/analytics" style={{ fontSize: 12, color: "var(--scw-muted)", textDecoration: "none" }} data-testid="capi-back">
        {"← Analytics"}
      </Link>
      <h1 style={{ fontSize: 28, margin: "10px 0 20px 0" }}>Conversion tracking</h1>
    </>
  );

  if (loading && !data) return <AdminLayout>{header}{"Loading…"}</AdminLayout>;
  if (err && !data) {
    return (
      <AdminLayout>
        {header}
        <div style={{ padding: 24, border: "1px solid var(--scw-line)", background: "#fcf8f4" }} data-testid="capi-load-error">
          <p className="scw-eyebrow" style={{ marginBottom: 6, color: "#a13b3b" }}>Conversion data temporarily unavailable</p>
          <p style={{ margin: "0 0 12px 0", fontSize: 13 }}>{err}</p>
          <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "6px 14px" }} onClick={() => load()}>Retry</button>
        </div>
      </AdminLayout>
    );
  }

  const cov = data.coverage;
  const log = data.log;

  return (
    <AdminLayout>
      {header}
      <RangePicker range={range} months={data.available_months} loading={loading} onChange={onRangeChange} testIdPrefix="capi" />
      {err && <p style={{ fontSize: 12, color: "#a13b3b", margin: "-12px 0 16px 0" }} data-testid="capi-range-error">{err}</p>}

      <h2 style={{ fontSize: 18, margin: "8px 0 16px 0" }}>Sent to Google and Meta</h2>
      <ErrorBoundary label="Coverage">
        <div className="scw-card" style={{ padding: 0, marginBottom: 32 }}>
          <CoverageTable coverage={cov} />
          <p style={{ fontSize: 11, color: "var(--scw-muted)", padding: "12px 18px", margin: 0, lineHeight: 1.6 }}>
            Leads created in this range. Eligible for Qualified lead: reached Contacted or In Progress, the statuses that
            trigger the upload. Eligible for Won: Closed Won with a sale value. Read from each lead&apos;s sent flags, so these
            figures survive deploys. Leads from before a platform&apos;s sending began show as not sent.
          </p>
        </div>
      </ErrorBoundary>

      {cov.gaps_total > 0 && (
        <>
          <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Not sent</h2>
          <div className="scw-card" style={{ padding: 0, marginBottom: 32 }} data-testid="capi-gaps">
            <div className="scw-table-wrap">
              <table className="scw-table">
                <thead><tr><th>Lead date</th><th>Lead</th><th>Event</th><th>Status now</th><th>Missing</th></tr></thead>
                <tbody>
                  {cov.gaps.map((g) => (
                    <tr key={`${g.lead_id}-${g.kind}`}>
                      <td style={{ whiteSpace: "nowrap" }}>{g.date}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 12 }}>{g.lead_id.slice(0, 8)}</td>
                      <td>{g.kind}</td>
                      <td>{g.status}</td>
                      <td>
                        {g.missing.join(", ")}
                        {g.no_match_key && (
                          <div style={{ fontSize: 11, color: "var(--scw-muted)" }}>Google needs an email, phone or click ID</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {cov.gaps_total > cov.gaps.length && (
              <p style={{ fontSize: 11, color: "var(--scw-muted)", padding: "12px 18px", margin: 0 }}>
                Showing the newest {cov.gaps.length} of {cov.gaps_total}.
              </p>
            )}
          </div>
        </>
      )}

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Duplicate uploads to Google</h2>
      <div className="scw-card" style={{ padding: 24, marginBottom: 32 }}>
        <BridgeCard bridge={data.bridge} fixLive={data.bridge_fix_live} />
        <p style={{ fontSize: 11, color: "var(--scw-muted)", margin: "16px 0 0 0", lineHeight: 1.6 }}>
          Order IDs that appear more than once in the bridge sheet Google imports from, whatever the date of the lead.
          Google rejects a repeated Order ID, so revenue is not double counted, but each one shows as an import error.
          Refreshes every 10 minutes.
        </p>
      </div>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Send log</h2>
      <div className="scw-card" style={{ padding: "12px 24px", marginBottom: 12, background: "#fcf8f4", borderColor: "#e8d9c5" }}>
        <p style={{ fontSize: 12, margin: "10px 0", color: AMBER }} data-testid="capi-log-note">{log.note}</p>
      </div>
      <div className="scw-card" style={{ padding: 24, marginBottom: 16 }} data-testid="capi-log-summary">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 24 }}>
          <LogSummary title="Google" counts={log.google} />
          <LogSummary title="Meta" counts={log.meta} />
          <LogSummary title="Hugo buy-in (Meta)" counts={log.hugo_buyin} />
        </div>
      </div>
      {(log.recent || []).length > 0 && (
        <div className="scw-card" style={{ padding: 0, marginBottom: 32 }} data-testid="capi-log">
          <div className="scw-table-wrap">
            <table className="scw-table">
              <thead><tr><th>Time</th><th>Platform</th><th>Event</th><th>Lead</th><th>Result</th></tr></thead>
              <tbody>
                {log.recent.map((r, i) => (
                  <tr key={`${r.ts}-${r.lead_id}-${i}`}>
                    <td style={{ whiteSpace: "nowrap" }}>{(r.ts || "").slice(0, 16).replace("T", " ")}</td>
                    <td>{PLATFORM[r.channel] || r.channel}</td>
                    <td>{r.event}</td>
                    <td style={{ fontFamily: "monospace", fontSize: 12 }}>{(r.lead_id || "").slice(0, 8)}</td>
                    <td style={{ color: r.status === "failed" ? "#a13b3b" : "inherit" }}>
                      {r.status}{r.reason ? <span style={{ color: "var(--scw-muted)" }}>{` (${r.reason})`}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
